import { on } from '@ember/modifier';
import { set, setProperties } from '@ember/object';
// eslint-disable-next-line ember/no-at-ember-render-modifiers
import didInsert from '@ember/render-modifiers/modifiers/did-insert';
import { LinkTo } from '@ember/routing';
import { service } from '@ember/service';
import type StoreService from '@ember-data/store';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import type { Task } from 'ember-concurrency';
import { dropTask } from 'ember-concurrency';
import { and, not } from 'ember-truth-helpers';
import SlideToggle from 'garaje/components/slide-toggle';
import MessageBox from 'garaje/components/ui/classic/message-box';
import SettingsPanel from 'garaje/components/ui/classic/settings-panel';
import type LocationModel from 'garaje/models/location';
import type LocationRoleModel from 'garaje/models/location-role';
import type ImpressionsService from 'garaje/services/impressions';
import { IMPRESSION_NAMES } from 'garaje/utils/enums';
import { RECEPTIONIST } from 'garaje/utils/roles';

interface PreRegRequiredSignature {
  Args: {
    location: LocationModel;
    saveTask: Task<void, [LocationModel]>;
  };
}

export default class PreRegRequired extends Component<PreRegRequiredSignature> {
  @service declare impressions: ImpressionsService;
  @service declare store: StoreService;

  @tracked hasReceptionist = false;
  @tracked isOpen = false;

  /**
   * enables / disables button visibility
   */
  get isDisableButtonVisible(): boolean {
    const { location } = this.args;

    return (
      this.isOpen &&
      location.preRegistrationRequiredEnabled &&
      !('preRegistrationRequiredEnabled' in location.changedAttributes())
    );
  }

  cancel = (): void => {
    const { location } = this.args;
    if (<boolean>(<unknown>location.hasDirtyAttributes)) {
      location.rollbackAttributes();
    }
    this.isOpen = false;
  };

  toggleHostApprovalEnabled = (): void => {
    const { location } = this.args;
    set(location, 'hostApprovalEnabled', !location.hostApprovalEnabled);
  };

  enablePreregistrationRequired = (): void => {
    const { location } = this.args;
    set(location, 'preRegistrationRequiredEnabled', true);
    this.isOpen = true;
  };

  disablePreregistrationRequired = async (): Promise<void> => {
    const { location } = this.args;
    setProperties(location, { preRegistrationRequiredEnabled: false, hostApprovalEnabled: false });
    await this.saveLocation();
  };

  saveLocation = async (): Promise<void> => {
    const { location, saveTask } = this.args;
    await saveTask.perform(location);
    this.isOpen = false;
    await this.impressions.postImpression.perform(
      IMPRESSION_NAMES.VR_INVITES_PRE_REGISTRATION_REQUIRED[
        location.preRegistrationRequiredEnabled ? 'ENABLED' : 'DISABLED'
      ],
    );
  };

  /**
   * Task used for loading receptionists from the server if they don't already exist in the store
   */
  loadFrontDeskAdmin = dropTask(async () => {
    const { location } = this.args;
    const cachedReceptionists = this.store.peekAll('location-role').filter((role) => role.roleName === RECEPTIONIST);
    let receptionists: LocationRoleModel[] = [];

    if (!cachedReceptionists.length) {
      receptionists = (
        await this.store.query('location-role', {
          filter: {
            location: location.id,
            roles: RECEPTIONIST,
          },
        })
      ).slice();
    }

    this.hasReceptionist = cachedReceptionists.length > 0 || receptionists.length > 0;
  });

  <template>
    <SettingsPanel
      {{didInsert this.loadFrontDeskAdmin.perform}}
      @isOpen={{this.isOpen}}
      data-test-pre-reg-required-panel
      ...attributes
      as |sp|
    >
      {{#let sp.header sp.body sp.footer sp.isOpen as |SPHeader SPBody SPFooter SPisOpen|}}
        <SPHeader
          @iconUri="/assets/images/features/prereg-invites-required.svg"
          @title="Approval required for unexpected visitors"
          @description="Visitors who aren’t invited must be approved by an admin."
          @onCancel={{this.cancel}}
        >
          {{#if SPisOpen}}
            <button
              data-test-pre-reg-required-toggle-save
              type="button"
              class="btn btn--small btn--brand"
              disabled={{@saveTask.isRunning}}
              {{on "click" this.saveLocation}}
            >
              {{if @saveTask.isRunning "Saving" "Save"}}
            </button>
          {{else}}
            <button
              data-test-pre-reg-required-toggle-open
              type="button"
              class="btn btn--small btn--brand"
              disabled={{@saveTask.isRunning}}
              {{on "click" this.enablePreregistrationRequired}}
            >
              {{if @location.preRegistrationRequiredEnabled "Edit" "Enable"}}
            </button>
          {{/if}}
        </SPHeader>
        <SPBody as |SPContent|>
          <SPContent @style="inset" class="my-8 {{if this.isDisableButtonVisible 'mb-4'}}">
            <div class="flex" style="max-height: 11rem;">
              <div class="flex flex-col max-w-3xl host-approval">
                <h4>Let hosts approve unexpected visitors via Slack</h4>
                <p class="mr-16">
                  Notify hosts of unexpected visitors and instantly approve the visit or flag it to an administrator
                  based on the host’s reply.
                </p>
              </div>
              <SlideToggle
                class="mr-4 host-approval"
                @isChecked={{@location.hostApprovalEnabled}}
                @onClick={{this.toggleHostApprovalEnabled}}
                @isDisabled={{@saveTask.isRunning}}
              />
            </div>
            {{#if
              (and (not this.loadFrontDeskAdmin.isRunning) (not this.hasReceptionist) @location.hostApprovalEnabled)
            }}
              <MessageBox @type="warning" @header="No Front Desk Admin" class="mb-1 mt-5" data-test-fd-admin-alerts>
                <p class="mb-0">
                  You need to have a Front Desk admin to receive alerts. Assign one from your
                  <br />
                  <LinkTo @route="manage.admin-users" data-test-fd-admin-employee-directory>employee directory</LinkTo>.
                </p>
              </MessageBox>
            {{/if}}
          </SPContent>
          {{#if this.isDisableButtonVisible}}
            <SPFooter>
              <button
                type="button"
                data-test-pre-reg-required-disable
                class="btn btn--small btn--outlineFill"
                disabled={{@saveTask.isRunning}}
                {{on "click" this.disablePreregistrationRequired}}
              >
                Disable
              </button>
            </SPFooter>
          {{/if}}
        </SPBody>
      {{/let}}
    </SettingsPanel>
  </template>
}
